import { useEffect, useState } from 'react'
import { Link, RefreshCw, Upload, Download, CheckCircle2, AlertCircle, Copy } from 'lucide-react'
import api from '../lib/api'

interface EstablishmentRow {
  id: string
  name: string
  googleSheetId: string | null
  googleSheetLastPushAt: string | null
  googleSheetLastPullAt: string | null
}

interface StatusResponse {
  serviceAccountEmail: string | null
  serviceAccountConfigured: boolean
  establishments: EstablishmentRow[]
}

function extractSpreadsheetId(input: string): string {
  const trim = input.trim()
  // URL forme : https://docs.google.com/spreadsheets/d/<ID>/edit#gid=...
  const m = trim.match(/spreadsheets\/d\/([a-zA-Z0-9-_]+)/)
  if (m) return m[1]
  return trim
}

export default function SheetsSync() {
  const [status, setStatus] = useState<StatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [inputs, setInputs] = useState<Record<string, string>>({})

  async function load() {
    setLoading(true)
    try {
      const r = await api.get('/sheets/status')
      setStatus(r.data)
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [])

  async function connect(establishmentId: string) {
    setError(null); setInfo(null)
    const raw = inputs[establishmentId] || ''
    if (!raw.trim()) { setError('Colle l\'URL ou l\'ID de ton Google Sheets'); return }
    const spreadsheetId = extractSpreadsheetId(raw)
    setBusyId(establishmentId)
    try {
      const r = await api.post('/sheets/connect', { establishmentId, spreadsheetId })
      setInfo(`Connecté à "${r.data.title}"`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setBusyId(null)
    }
  }

  async function init(establishmentId: string) {
    if (!confirm('Cette action va RÉINITIALISER les 12 onglets mensuels du Sheets (mise en page, couleurs, employés). Si tu as des données dedans, elles seront effacées. Continuer ?')) return
    setError(null); setInfo(null); setBusyId(establishmentId)
    try {
      // Récupère l'ordre des employés défini dans la page Planning (localStorage)
      let employeeOrder: string[] = []
      try {
        const raw = localStorage.getItem(`employee_order_${establishmentId}`)
        if (raw) employeeOrder = JSON.parse(raw)
      } catch { /* ignore */ }
      const r = await api.post('/sheets/init', { establishmentId, year: new Date().getFullYear(), employeeOrder })
      setInfo(`Layout initialisé : ${r.data.monthsCreated} mois × ${r.data.employees} employés (ordre app respecté).`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setBusyId(null)
    }
  }

  async function push(establishmentId: string) {
    setError(null); setInfo(null); setBusyId(establishmentId)
    try {
      const r = await api.post('/sheets/push', { establishmentId })
      setInfo(`${r.data.shiftsWritten || 0} horaires + ${r.data.absencesWritten || 0} absences écrits du ${r.data.from} au ${r.data.to}.`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setBusyId(null)
    }
  }

  async function pull(establishmentId: string) {
    if (!confirm('Importe tous les horaires saisis dans le Google Sheets vers l\'app. Les shifts précédemment importés seront remplacés. Continuer ?')) return
    setError(null); setInfo(null); setBusyId(establishmentId)
    try {
      const r = await api.post('/sheets/pull', { establishmentId, year: new Date().getFullYear() })
      setInfo(`Import : ${r.data.shiftsCreated} shifts créés, ${r.data.shiftsDeleted} anciens supprimés (${r.data.monthsRead.length} mois lus).`)
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message)
    } finally {
      setBusyId(null)
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => { setInfo('Copié.'); setTimeout(() => setInfo(null), 1500) })
  }

  if (loading) return <div className="p-8 text-gray-500">Chargement…</div>
  if (!status) return <div className="p-8 text-red-500">{error}</div>

  const sa = status.serviceAccountEmail

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-xl font-bold text-gray-900 mb-1">Sync Google Sheets</h1>
      <p className="text-sm text-gray-500 mb-6">Lie un Google Sheets à chaque établissement pour saisir ton planning à la main et que l'app y écrive automatiquement les congés/absences validés.</p>

      {/* Bandeau service account */}
      <div className={`mb-6 p-4 rounded-lg border ${status.serviceAccountConfigured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        {status.serviceAccountConfigured && sa ? (
          <>
            <div className="flex items-center gap-2 text-sm font-medium text-green-800 mb-2">
              <CheckCircle2 size={16} /> Compte de service Google configuré
            </div>
            <div className="text-xs text-gray-700 mb-2">Pour que l'app puisse lire/écrire tes Sheets, partage chacun d'eux <strong>en éditeur</strong> avec cette adresse :</div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-1.5">
              <code className="text-xs flex-1 break-all">{sa}</code>
              <button onClick={() => copyToClipboard(sa)} className="text-blue-600 hover:text-blue-800" title="Copier">
                <Copy size={14} />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 mb-1">
              <AlertCircle size={16} /> Compte de service Google non configuré
            </div>
            <div className="text-xs text-gray-700">L'admin doit définir la variable d'environnement <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> sur le backend (clé JSON du service account).</div>
          </>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}
      {info  && <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">{info}</div>}

      <div className="space-y-4">
        {status.establishments.map(est => (
          <div key={est.id} className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">{est.name}</h2>
              {est.googleSheetId
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Lié</span>
                : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Non lié</span>}
            </div>

            {est.googleSheetId && (
              <div className="text-xs text-gray-500 mb-3">
                ID : <code className="bg-gray-100 px-1 py-0.5 rounded">{est.googleSheetId}</code>
                {est.googleSheetLastPushAt && <> — dernière écriture : {new Date(est.googleSheetLastPushAt).toLocaleString('fr-FR')}</>}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                type="text"
                placeholder="URL ou ID Google Sheets…"
                value={inputs[est.id] || ''}
                onChange={e => setInputs(s => ({ ...s, [est.id]: e.target.value }))}
                className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                disabled={busyId === est.id}
              />
              <button
                onClick={() => connect(est.id)}
                disabled={!status.serviceAccountConfigured || busyId === est.id}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                <Link size={14} /> Connecter
              </button>
            </div>

            {est.googleSheetId && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => init(est.id)}
                  disabled={busyId === est.id}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 disabled:opacity-40 flex items-center gap-1.5">
                  <RefreshCw size={14} /> Initialiser le layout (12 mois)
                </button>
                <button
                  onClick={() => push(est.id)}
                  disabled={busyId === est.id}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-40 flex items-center gap-1.5">
                  <Upload size={14} /> Pousser les absences/congés
                </button>
                <button
                  onClick={() => pull(est.id)}
                  disabled={busyId === est.id}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1.5">
                  <Download size={14} /> Importer les horaires saisis
                </button>
                <a
                  href={`https://docs.google.com/spreadsheets/d/${est.googleSheetId}/edit`}
                  target="_blank" rel="noreferrer"
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1.5">
                  Ouvrir le Sheets ↗
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600 leading-relaxed">
        <div className="font-medium text-gray-800 mb-2">Procédure pour démarrer (à faire 1 seule fois)</div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Sur Google Cloud Console, crée un projet, active l'API "Google Sheets" et "Google Drive".</li>
          <li>Crée un <strong>compte de service</strong>, télécharge la clé JSON.</li>
          <li>Sur Railway, ajoute la variable d'environnement <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> avec le contenu complet de ce JSON.</li>
          <li>Crée un Google Sheets vide par établissement (ex : "Planning Cassis", "Planning Six-Fours").</li>
          <li>Partage chaque Sheets <strong>en éditeur</strong> avec l'adresse du service account (affichée plus haut une fois la variable définie).</li>
          <li>Reviens ici, colle l'URL de chaque Sheets dans le champ correspondant, clique <strong>Connecter</strong>.</li>
          <li>Clique <strong>Initialiser le layout</strong> pour créer les 12 onglets mensuels avec tes employés.</li>
          <li>Clique <strong>Pousser les absences/congés</strong> pour pré-remplir les contraintes connues.</li>
        </ol>
      </div>
    </div>
  )
}
